import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { computed, effect, signal, untracked } from '@preact/signals-core';
import { nothing, type TemplateResult } from 'lit';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import {
  CanvasViewportParticipantController,
  type CanvasViewportRowAnchorRect,
  type CanvasViewportTargetPosition,
  createElementCanvasViewportTarget,
  createRowAnchoredCanvasViewportTarget,
} from '../../../core/canvas-viewport.js';
import { dv } from '../../../core/common/dv-css.js';
import {
  type GroupTrait,
  groupTraitKey,
} from '../../../core/group-by/trait.js';
import { createUniComponentFromWebComponent } from '../../../core/index.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
} from '../../../core/view/data-view-base.js';
import {
  laidOutDataViewBodyState,
  measureDataViewBodyIntrinsicHeight,
} from '../../../core/view/data-view-body.js';
import { revealRowWindow } from '../../../core/view/reveal-row-window.js';
import {
  type TableSingleView,
  TableViewAreaSelection,
  TableViewRowSelection,
  type TableViewSelectionWithType,
} from '../../index.js';
import {
  type DataViewHorizontalScrollbar,
  getHorizontalWheelDelta,
} from '../../shared/horizontal-scrollbar.js';
import { TABLE_ROW_HEIGHT } from '../consts.js';
import { handleTableWheel } from '../utils/wheel.js';
import { TableClipboardController } from './controller/clipboard.js';
import { TableDragController } from './controller/drag.js';
import { TableHotkeysController } from './controller/hotkeys.js';
import { TableSelectionController } from './controller/selection.js';
import { TableGroupFooter } from './group/bottom/group-footer.js';
import { TableGroupHeader } from './group/top/group-header.js';
import {
  createVirtualTableLayoutSnapshot,
  getColumnRevealScrollDelta,
  reconcileTableSelectionAfterColumnChange,
} from './layout-snapshot.js';
import { DatabaseCellContainer } from './row/cell.js';
import { TableRowHeader } from './row/row-header.js';
import { TableRowLast } from './row/row-last.js';
import * as styles from './table-view-css.js';
import type {
  TableCellData,
  TableGrid,
  TableGroupData,
  TableRowData,
} from './types.js';
import {
  GridVirtualScroll,
  traceTableLayout,
} from './virtual/virtual-scroll.js';

const TABLE_CANVAS_X_SCROLL_TARGET = 'table-x';
const TABLE_CANVAS_Y_SCROLL_TARGET = 'table-y';
const RELATION_CELL_MAX_VISIBLE_ENTRIES = 10;
const RELATION_CELL_LINE_HEIGHT = 24;
const RELATION_CELL_MORE_COUNT_HEIGHT = 18;
const RELATION_CELL_LINE_GAP = 4;
const RELATION_CELL_VERTICAL_PADDING = 4;
const CANVAS_VIEWPORT_TARGET_CHANGE_QUIET_MS = 120;
const TABLE_LAYOUT_CACHE_WRITE_DELAY_MS = 200;

function estimateRelationCellHeight(value: unknown): number {
  if (!Array.isArray(value) || value.length === 0) {
    return TABLE_ROW_HEIGHT;
  }
  const visibleCount = Math.min(
    value.length,
    RELATION_CELL_MAX_VISIBLE_ENTRIES
  );
  const hasMoreCount = value.length > visibleCount;
  const lineCount = visibleCount + (hasMoreCount ? 1 : 0);
  return Math.max(
    TABLE_ROW_HEIGHT,
    RELATION_CELL_VERTICAL_PADDING +
      visibleCount * RELATION_CELL_LINE_HEIGHT +
      (hasMoreCount ? RELATION_CELL_MORE_COUNT_HEIGHT : 0) +
      Math.max(0, lineCount - 1) * RELATION_CELL_LINE_GAP
  );
}

function createTableLayoutSignature(view: TableSingleView): string {
  return JSON.stringify({
    columns: view.properties$.value.map(property => ({
      id: property.id,
      type: property.type$.value,
      width: property.width$.value,
    })),
    cellDisplayMode: view.cellDisplayMode$.value,
  });
}

export class VirtualTableViewUILogic extends DataViewUILogicBase<
  TableSingleView,
  TableViewSelectionWithType
> {
  ui$ = signal<TableViewUI | undefined>();
  clipboardController = new TableClipboardController(this);
  dragController = new TableDragController(this);
  hotkeysController = new TableHotkeysController(this);
  selectionController = new TableSelectionController(this);

  virtualScroll$ = signal<TableGrid>();

  /** The body is a table only once its grid has laid out its rows. */
  readonly bodyState$ = computed(() =>
    laidOutDataViewBodyState(
      this.virtualScroll$.value?.lastGroupBottom$.value,
      'data'
    )
  );

  tableViewportWidth$ = signal(0);

  layoutSnapshot$ = computed(() =>
    createVirtualTableLayoutSnapshot(
      this.view.properties$.value,
      this.tableViewportWidth$.value
    )
  );

  columns$ = computed(() => this.layoutSnapshot$.value.gridColumns);

  groupTrait$ = computed(() => {
    return this.view.traitGet(groupTraitKey);
  });

  groups$ = computed(() => {
    const groupTrait = this.groupTrait$.value;
    if (!groupTrait?.groupsDataList$.value) {
      return [
        {
          id: '',
          rows: this.view.getBoundedRowIdsWindow().rowIds,
        },
      ];
    }
    const groups = groupTrait.groupsDataList$.value.filter(
      (
        group
      ): group is NonNullable<
        (typeof groupTrait.groupsDataList$.value)[number]
      > => group != null
    );
    const visibleRowIds = new Set(this.view.getBoundedRowIdsWindow().rowIds);
    return groups.map(group => ({
      id: group.key,
      rows: group.rows
        .map(v => v.rowId)
        .filter(rowId => visibleRowIds.has(rowId)),
    }));
  });

  clearSelection = () => {
    this.selectionController.clear();
  };

  private stopRowReveal: (() => void) | undefined;

  /**
   * Pages to the row, then scrolls the grid to it once the grid has laid it
   * out. False when the view does not show the row.
   */
  override revealRow(rowId: string): boolean {
    if (!revealRowWindow(this.view, rowId)) return false;
    this.stopRowReveal?.();
    let done = false;
    const stop = effect(() => {
      if (done) return;
      const grid = this.virtualScroll$.value;
      const group = this.groups$.value.find(each => each.rows.includes(rowId));
      if (!grid || !group) return;
      const row = grid.getRow(group.id, rowId);
      const top = row.top$.value;
      const bottom = row.bottom$.value;
      if (top == null || bottom == null) return;
      done = true;
      untracked(() => grid.container.revealVerticalRange(top, bottom));
      queueMicrotask(finish);
    });
    const finish = () => {
      stop();
      if (this.stopRowReveal === finish) this.stopRowReveal = undefined;
    };
    if (!done) this.stopRowReveal = finish;
    return true;
  }

  private addedRowFocusGeneration = 0;
  private cancelAddedRowFocus: (() => void) | undefined;

  async focusAddedRow(rowId: string, groupKey?: string): Promise<void> {
    const generation = ++this.addedRowFocusGeneration;
    this.cancelAddedRowFocus?.();
    traceTableLayout('new-row-focus', { phase: 'entered' });
    // The logic survives replacement of the presentation element during ACK.
    // Focus belongs to the latest connected renderer, never the clicked footer.
    await this.view.manager.dataSource.flushLocalWrites();
    traceTableLayout('new-row-focus', {
      phase: 'local-ack',
      connected: this.ui$.peek()?.isConnected ?? false,
      rootConnected: this.root.dataViewRenderer?.isConnected ?? false,
    });
    if (generation !== this.addedRowFocusGeneration) return;
    if (!this.view.getBoundedRowIdsWindow().rowIds.includes(rowId)) {
      this.view.setRowWindowOffset(this.view.rowCount$.value);
      traceTableLayout('new-row-focus', {
        phase: 'window-requested',
        offset: this.view.rowWindowOffset$.value,
        total: this.view.rowCount$.value,
      });
    }
    this.selectionController.selection = undefined;
    let frame: number | undefined;
    let stop: (() => void) | undefined;
    let completed = false;
    const cancel = (reason = 'complete') => {
      traceTableLayout('new-row-focus', { phase: 'cancelled', reason });
      completed = true;
      stop?.();
      if (frame != null) cancelAnimationFrame(frame);
      if (this.cancelAddedRowFocus === cancel)
        this.cancelAddedRowFocus = undefined;
    };
    this.cancelAddedRowFocus = cancel;
    stop = effect(() => {
      const ui = this.ui$.value;
      const root = this.root.dataViewRenderer;
      const currentView = this.root.currentView$.value;
      if (completed) return;
      if (currentView !== this || generation !== this.addedRowFocusGeneration) {
        cancel('view-changed');
        return;
      }
      if (!ui?.isConnected || !root?.isConnected) {
        // A replacement connects in the same render turn. Closing the view
        // releases observers; it must never steal focus after navigation.
        queueMicrotask(() => {
          if (!this.root.dataViewRenderer?.isConnected)
            cancel('root-disconnected');
        });
        return;
      }
      const columns = this.layoutSnapshot$.value.dataColumns;
      const index = columns.findIndex(
        column => column.property.type$.value === 'title'
      );
      const row = this.selectionController.getRow(groupKey, rowId);
      const rowIndex = row?.rowIndex$.value;
      traceTableLayout('new-row-focus', {
        phase: 'grid',
        index: rowIndex ?? -1,
      });
      const titleColumn = columns[index];
      if (!titleColumn || rowIndex == null || rowIndex < 0) return;
      // Revealing a virtual column schedules its DOM mount. Observe the real
      // cell renderer's signal before entering edit mode; grid coordinates
      // alone do not mean the editor exists yet.
      const rowTop = row?.top$.value;
      const rowBottom = row?.bottom$.value;
      untracked(() => {
        ui.revealColumn(titleColumn.id);
        if (rowTop != null && rowBottom != null)
          this.virtualScroll$
            .peek()
            ?.container.revealVerticalRange(rowTop, rowBottom);
      });
      const gridCell = this.selectionController.getCell(
        groupKey,
        rowId,
        titleColumn.id
      );
      const container = gridCell?.element.element;
      const renderer =
        container instanceof DatabaseCellContainer ? container.cell : undefined;
      traceTableLayout('new-row-focus', {
        phase: 'cell-ready',
        ready: !!renderer,
        connected: container?.isConnected ?? false,
        rowTop: rowTop ?? -1,
        rowBottom: rowBottom ?? -1,
        cellLeft: gridCell?.left$.value ?? -1,
        cellRight: gridCell?.right$.value ?? -1,
        visible: gridCell?.isVisible$.value ?? false,
        viewport: this.virtualScroll$.value?.container.viewport$.value,
      });
      if (!renderer || !container?.isConnected) return;
      if (frame != null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = undefined;
        traceTableLayout('new-row-focus', {
          phase: 'frame',
          completed,
          connected: ui.isConnected,
          current: this.ui$.peek() === ui,
        });
        if (completed || !ui.isConnected || this.ui$.peek() !== ui) return;
        this.selectionController.selection = TableViewAreaSelection.create({
          groupKey,
          focus: { rowIndex, columnIndex: index },
          isEditing: true,
        });
        this.selectionController
          .getCellContainer(groupKey, rowIndex, index)
          ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        const selection = this.selection$.peek();
        traceTableLayout('new-row-focus', {
          phase: 'focused',
          editing:
            container instanceof DatabaseCellContainer &&
            container.isEditing$.peek(),
          selectionEditing:
            this.selectionController.selection?.selectionType === 'area' &&
            this.selectionController.selection.isEditing,
          rootSelectionEditing:
            selection?.selectionType === 'area' && selection.isEditing,
          resolvedSameCell:
            this.selectionController.getCellContainer(
              groupKey,
              rowIndex,
              index
            ) === container,
        });
        cancel();
      });
    });
    if (completed) stop();
  }

  addRow = (position: InsertToPosition) => {
    if (!this.view.rowCanAdd()) {
      const reason = this.view.rowAddDisabledReason();
      if (reason) this.root.config.notification.toast(reason);
      return;
    }
    return this.view.rowAdd(position);
  };

  focusFirstCell = () => {
    this.selectionController.focusFirstCell();
  };

  showIndicator = (evt: MouseEvent) => {
    return this.dragController.showIndicator(evt) != null;
  };

  hideIndicator = () => {
    this.dragController.dropPreview.remove();
  };

  moveTo = (id: string, evt: MouseEvent) => {
    const result = this.dragController.getInsertPosition(evt);
    if (result) {
      const row = this.view.rowGetOrCreate(id);
      row.move(result.position, undefined, result.groupKey);
    }
  };

  onWheel = (event: WheelEvent) => {
    handleTableWheel(event);
  };

  renderAddGroup = (groupHelper: GroupTrait) => {
    const addGroup = groupHelper.addGroup;
    if (!addGroup) {
      return;
    }
    const add = (e: MouseEvent) => {
      const ele = e.currentTarget as HTMLElement;
      popMenu(popupTargetFromElement(ele), {
        options: {
          preferDesktop: true,
          items: [
            menu.input({
              onComplete: text => {
                const column = groupHelper.property$.value;
                if (column) {
                  column.dataUpdate(() =>
                    addGroup({
                      text,
                      oldData: column.data$.value,
                      dataSource: this.view.manager.dataSource,
                    })
                  );
                }
              },
            }),
          ],
        },
      });
    };
    return html` <div style="display:flex;">
      <div class="${dv.hover} ${dv.round8} ${styles.addGroup}" @click="${add}">
        <div class="${dv.icon16}" style="display:flex;">${AddCursorIcon()}</div>
        <div>New Group</div>
      </div>
    </div>`;
  };

  initVirtualScroll(
    scrollContainers: {
      xScrollContainer: HTMLElement;
      yScrollContainer: HTMLElement;
    },
    ui: TableViewUI
  ) {
    if (this.virtualScroll$.value) {
      return;
    }
    const tableLayoutSignature = createTableLayoutSignature(this.view);
    const initialLayoutState =
      this.view.manager.dataSource.getTableViewLayoutState(
        this.view.id,
        tableLayoutSignature
      );
    ui.setCachedIntrinsicContentHeight(
      initialLayoutState?.intrinsicContentHeight
    );
    const initialRowHeights = initialLayoutState
      ? new Map(Object.entries(initialLayoutState.rowHeights))
      : undefined;
    let pendingCachedRowHeights = initialRowHeights
      ? new Map(initialRowHeights)
      : new Map<string, number>();
    let layoutCacheWriteTimer: ReturnType<typeof setTimeout> | undefined;
    let lastWrittenTableLayoutSnapshot: string | undefined;
    let virtualScroll!: GridVirtualScroll<
      TableGroupData,
      TableRowData,
      TableCellData
    >;
    const writeTableLayoutCache = () => {
      const intrinsicContentHeight = virtualScroll.lastGroupBottom$.value;
      const rowHeights = Object.fromEntries(pendingCachedRowHeights);
      if (
        intrinsicContentHeight == null &&
        Object.keys(rowHeights).length === 0
      ) {
        return;
      }
      const snapshot = JSON.stringify({
        intrinsicContentHeight,
        rowHeights,
      });
      if (snapshot === lastWrittenTableLayoutSnapshot) {
        return;
      }
      lastWrittenTableLayoutSnapshot = snapshot;
      this.view.manager.dataSource.setTableViewLayoutState(this.view.id, {
        columnLayoutSignature: tableLayoutSignature,
        intrinsicContentHeight,
        measuredAt: Date.now(),
        rowHeights,
        version: 1,
      });
    };
    const scheduleTableLayoutCacheWrite = () => {
      if (layoutCacheWriteTimer != null) {
        clearTimeout(layoutCacheWriteTimer);
      }
      layoutCacheWriteTimer = setTimeout(() => {
        layoutCacheWriteTimer = undefined;
        writeTableLayoutCache();
      }, TABLE_LAYOUT_CACHE_WRITE_DELAY_MS);
    };
    virtualScroll = new GridVirtualScroll<
      TableGroupData,
      TableRowData,
      TableCellData
    >({
      initGroupData: group => ({
        hover$: computed(() => {
          const headerHover = group.data.headerHover$.value;
          if (headerHover) {
            return true;
          }
          const footerHover = group.data.footerHover$.value;
          if (footerHover) {
            return true;
          }
          return group.rows$.value.some(row => row.data.hover$.value);
        }),
        headerHover$: signal(false),
        footerHover$: signal(false),
      }),
      initRowData: row => ({
        hover$: computed(() => {
          return row.cells$.value.some(cell => cell.data.hover$.value);
        }),
        selected$: computed(() => {
          const selection = this.selection$.value;
          if (!selection || selection.selectionType !== 'row') {
            return false;
          }
          const groupId = row.group.groupId;
          return TableViewRowSelection.includes(selection, {
            id: row.rowId,
            groupKey: groupId ? groupId : undefined,
          });
        }),
      }),
      initCellData: () => ({
        hover$: signal(false),
        selected$: signal(false),
      }),
      columns$: this.columns$,
      groups$: this.groups$,
      createCell: cell => {
        if (cell.columnId === 'row-header') {
          const rowHeader = new TableRowHeader();
          rowHeader.gridCell = cell;
          rowHeader.tableViewLogic = this;
          return rowHeader;
        }
        if (cell.columnId === 'row-last') {
          const rowLast = new TableRowLast();
          rowLast.gridCell = cell;
          rowLast.tableViewLogic = this;
          return rowLast;
        }
        const cellContainer = new DatabaseCellContainer();
        cellContainer.gridCell = cell;
        cellContainer.tableViewLogic = this;
        return cellContainer;
      },
      createGroup: {
        top: gridGroup => {
          const groupHeader = new TableGroupHeader();
          groupHeader.tableViewLogic = this;
          groupHeader.gridGroup = gridGroup;
          return groupHeader;
        },
        bottom: gridGroup => {
          const groupFooter = new TableGroupFooter();
          groupFooter.tableViewLogic = this;
          groupFooter.gridGroup = gridGroup;
          return groupFooter;
        },
      },
      fixedRowHeight$: signal(undefined),
      estimatedRowHeight$: signal(TABLE_ROW_HEIGHT),
      initialRowHeights,
      onRowHeightsChange: rowHeights => {
        pendingCachedRowHeights = new Map(rowHeights);
        ui.queueCanvasViewportTargetsChanged();
        scheduleTableLayoutCacheWrite();
      },
      xScrollContainer: scrollContainers.xScrollContainer,
      yScrollContainer: scrollContainers.yScrollContainer,
    });
    this.virtualScroll$.value = virtualScroll;
    let previousPropertyIds = this.layoutSnapshot$.peek().propertyIds;
    const stopLayoutReconciliation = effect(() => {
      const nextPropertyIds = this.layoutSnapshot$.value.propertyIds;
      if (
        previousPropertyIds.length === nextPropertyIds.length &&
        previousPropertyIds.every((id, index) => id === nextPropertyIds[index])
      ) {
        return;
      }
      const selection = reconcileTableSelectionAfterColumnChange(
        this.selectionController.selection,
        previousPropertyIds,
        nextPropertyIds
      );
      if (selection !== this.selectionController.selection) {
        this.selectionController.selection = selection;
      }
      previousPropertyIds = nextPropertyIds;
      ui.queueHorizontalScrollReconciliation();
    });
    ui.disposables.add(stopLayoutReconciliation);
    requestAnimationFrame(() => {
      if (virtualScroll) {
        virtualScroll.init();
        const stopIntrinsicHeightEffect = effect(() => {
          virtualScroll.lastGroupBottom$.value;
          ui.queueIntrinsicContentHeightChange();
          scheduleTableLayoutCacheWrite();
        });
        type RelationHeightGroup = (typeof virtualScroll.groups$.value)[number];
        const relationHeightEffects = new Map<
          RelationHeightGroup,
          Map<string, () => void>
        >();
        const stopRelationHeightStructureEffect = effect(() => {
          const relationProperties = this.view.properties$.value.filter(
            property => property.type$.value === 'relation'
          );
          const activeGroups = new Set<RelationHeightGroup>();
          for (const group of virtualScroll.groups$.value) {
            activeGroups.add(group);
            const groupEffects =
              relationHeightEffects.get(group) ?? new Map<string, () => void>();
            relationHeightEffects.set(group, groupEffects);
            const activeKeys = new Set<string>();
            for (const rowId of group.rowIds$.value) {
              for (const property of relationProperties) {
                const key = `${rowId}\u0000${property.id}`;
                activeKeys.add(key);
                if (!groupEffects.has(key)) {
                  groupEffects.set(
                    key,
                    effect(() => {
                      const value = this.view.cellGetOrCreate(
                        rowId,
                        property.id
                      ).value$.value;
                      group.updateCellHeight(
                        rowId,
                        property.id,
                        estimateRelationCellHeight(value),
                        {
                          allowCachedShrink: true,
                          preserveRestoredRowHeight: true,
                        }
                      );
                    })
                  );
                }
              }
            }
            for (const [key, dispose] of groupEffects) {
              if (!activeKeys.has(key)) {
                dispose();
                groupEffects.delete(key);
              }
            }
          }
          for (const [group, groupEffects] of relationHeightEffects) {
            if (!activeGroups.has(group)) {
              groupEffects.forEach(dispose => dispose());
              relationHeightEffects.delete(group);
            }
          }
        });
        ui.disposables.add(() => {
          if (layoutCacheWriteTimer != null) {
            clearTimeout(layoutCacheWriteTimer);
            layoutCacheWriteTimer = undefined;
          }
          writeTableLayoutCache();
          stopIntrinsicHeightEffect();
          stopRelationHeightStructureEffect();
          relationHeightEffects.forEach(groupEffects =>
            groupEffects.forEach(dispose => dispose())
          );
          relationHeightEffects.clear();
          virtualScroll.dispose();
          if (this.virtualScroll$.value === virtualScroll) {
            this.virtualScroll$.value = undefined;
          }
        });
      }
    });
  }

  renderer = createUniComponentFromWebComponent(TableViewUI);
}

export class TableViewUI extends DataViewUIBase<VirtualTableViewUILogic> {
  private readonly yScrollContainerRef: Ref<HTMLDivElement> = createRef();

  private readonly xScrollContainerRef: Ref<HTMLDivElement> = createRef();

  private readonly horizontalScrollbarRef: Ref<DataViewHorizontalScrollbar> =
    createRef();

  /**
   * Horizontal scroll position of the table body. Public so the sticky
   * header can follow programmatic scrolling (column-drag edge auto-scroll).
   */
  readonly xScrollLeft$ = signal(0);

  private readonly scrollContentWidth$ = signal(0);

  private readonly scrollViewportWidth$ = signal(0);

  private cachedIntrinsicContentHeight: number | undefined;

  private tableViewportResizeObserver: ResizeObserver | undefined;

  private canvasViewportTargetsChangedTimer:
    | ReturnType<typeof setTimeout>
    | undefined;

  private virtualScrollRenderQueued = false;

  private horizontalScrollReconciliationFrame: number | undefined;

  private readonly canvasViewport = new CanvasViewportParticipantController(
    this,
    'table',
    () => this.logic.view.featureFlags$.value.use_shared_canvas_viewport
  );

  private renderTable() {
    return this.logic.virtualScroll$.peek()?.content;
  }

  setCachedIntrinsicContentHeight(height: number | undefined): void {
    this.cachedIntrinsicContentHeight =
      typeof height === 'number' && Number.isFinite(height) && height > 0
        ? height
        : undefined;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.logic.ui$.value = this;
    this.logic.clipboardController.hostConnected();
    this.logic.dragController.hostConnected();
    this.logic.hotkeysController.hostConnected();
    this.logic.selectionController.hostConnected();
    this.classList.add(styles.tableView);
    if (this.logic.view.featureFlags$.value.use_shared_canvas_viewport) {
      this.dataset.canvasViewportParticipant = 'table';
    }
  }

  override disconnectedCallback(): void {
    if (this.logic.ui$.peek() === this) this.logic.ui$.value = undefined;
    this.tableViewportResizeObserver?.disconnect();
    this.tableViewportResizeObserver = undefined;
    if (this.canvasViewportTargetsChangedTimer != null) {
      clearTimeout(this.canvasViewportTargetsChangedTimer);
      this.canvasViewportTargetsChangedTimer = undefined;
    }
    this.canvasViewport.dispose();
    if (this.horizontalScrollReconciliationFrame != null) {
      cancelAnimationFrame(this.horizontalScrollReconciliationFrame);
      this.horizontalScrollReconciliationFrame = undefined;
    }
    super.disconnectedCallback();
  }

  protected override updated(): void {
    if (this.ensureVirtualScroll()) {
      this.queueVirtualScrollRender();
    }
    this.syncDataViewBodyResizeObserver({
      body: this.yScrollContainerRef.value,
      extraTargets: [
        this.xScrollContainerRef.value,
        this.logic.virtualScroll$.value?.content,
      ],
      observeBodyChildren: false,
    });
    this.syncCanvasViewportParticipants();
    this.queueIntrinsicContentHeightChange();
    const xScrollContainer = this.xScrollContainerRef.value;
    if (xScrollContainer) {
      this.scrollViewportWidth$.value = xScrollContainer.clientWidth;
      this.scrollContentWidth$.value = xScrollContainer.scrollWidth;
    }
  }

  private ensureVirtualScroll(): boolean {
    const xScrollContainer = this.xScrollContainerRef.value;
    const yScrollContainer = this.yScrollContainerRef.value;
    if (
      !xScrollContainer ||
      !yScrollContainer ||
      this.logic.virtualScroll$.peek()
    ) {
      return false;
    }
    this.observeTableViewportWidth(xScrollContainer);
    this.logic.initVirtualScroll(
      {
        xScrollContainer,
        yScrollContainer,
      },
      this
    );
    const content = this.logic.virtualScroll$.peek()?.content;
    if (content) this.tableViewportResizeObserver?.observe(content);
    return true;
  }

  private queueVirtualScrollRender(): void {
    if (this.virtualScrollRenderQueued) {
      return;
    }
    this.virtualScrollRenderQueued = true;
    queueMicrotask(() => {
      this.virtualScrollRenderQueued = false;
      if (this.isConnected) {
        this.requestUpdate();
      }
    });
  }

  private observeTableViewportWidth(container: HTMLElement): void {
    const syncWidth = () => {
      this.logic.tableViewportWidth$.value = container.clientWidth;
      this.scrollViewportWidth$.value = container.clientWidth;
      this.scrollContentWidth$.value = container.scrollWidth;
    };
    syncWidth();
    this.tableViewportResizeObserver?.disconnect();
    this.tableViewportResizeObserver = new ResizeObserver(syncWidth);
    this.tableViewportResizeObserver.observe(container);
  }

  applyCanvasViewportTarget(
    targetId: string,
    position: CanvasViewportTargetPosition
  ): boolean {
    return this.canvasViewport.applyTarget(targetId, position);
  }

  getCanvasViewportTargets(): Record<string, CanvasViewportTargetPosition> {
    return this.canvasViewport.getTargets();
  }

  queueCanvasViewportTargetsChanged(): void {
    if (this.canvasViewportTargetsChangedTimer != null) {
      clearTimeout(this.canvasViewportTargetsChangedTimer);
    }
    this.canvasViewportTargetsChangedTimer = setTimeout(() => {
      this.canvasViewportTargetsChangedTimer = undefined;
      this.canvasViewport.notifyTargetsChanged();
    }, CANVAS_VIEWPORT_TARGET_CHANGE_QUIET_MS);
  }

  queueHorizontalScrollReconciliation(): void {
    if (this.horizontalScrollReconciliationFrame != null) {
      cancelAnimationFrame(this.horizontalScrollReconciliationFrame);
    }
    this.horizontalScrollReconciliationFrame = requestAnimationFrame(() => {
      this.horizontalScrollReconciliationFrame = undefined;
      const container = this.xScrollContainerRef.value;
      if (!container) {
        return;
      }
      const maxScrollLeft = Math.max(
        0,
        container.scrollWidth - container.clientWidth
      );
      const nextScrollLeft = Math.min(container.scrollLeft, maxScrollLeft);
      if (container.scrollLeft !== nextScrollLeft) {
        container.scrollLeft = nextScrollLeft;
      }
      this.xScrollLeft$.value = nextScrollLeft;
    });
  }

  private syncCanvasViewportParticipants(): void {
    this.canvasViewport.sync([
      createElementCanvasViewportTarget(
        TABLE_CANVAS_X_SCROLL_TARGET,
        this.xScrollContainerRef.value,
        'x'
      ),
      createRowAnchoredCanvasViewportTarget(
        TABLE_CANVAS_Y_SCROLL_TARGET,
        this.yScrollContainerRef.value,
        'y',
        () => this.getTableRowAnchorRects()
      ),
    ]);
  }

  private getTableRowAnchorRects(): CanvasViewportRowAnchorRect[] {
    const grid = this.logic.virtualScroll$.value;
    if (!grid) {
      return [];
    }
    const rows: CanvasViewportRowAnchorRect[] = [];
    for (const group of grid.groups$.value) {
      for (const row of group.rows$.value) {
        const top = row.top$.value;
        const height = row.height$.value;
        if (top == null || height == null) {
          continue;
        }
        rows.push({
          height,
          rowId: row.rowId,
          top,
        });
      }
    }
    return rows;
  }

  override getIntrinsicContentHeight(visualScale = 1): number {
    const tableContainer = this.yScrollContainerRef.value;
    const gridHeight = this.logic.virtualScroll$.value?.lastGroupBottom$.value;
    // Canvas autosize must not depend on scrollHeight: in edgeless mode the
    // scroll container height is itself driven by the canvas block height.
    // Before the virtual grid has produced row metrics, scrollHeight is still
    // useful as a one-way bootstrap value. Once gridHeight exists, it is the
    // only data height source so host height cannot feed back into itself.
    const dataHeight =
      gridHeight ??
      this.cachedIntrinsicContentHeight ??
      tableContainer?.scrollHeight ??
      0;
    const scale = visualScale || 1;
    const bodyHeight = measureDataViewBodyIntrinsicHeight({
      body: tableContainer,
      visualScale: scale,
    });
    // The custom horizontal scrollbar is a separate in-flow sibling of the
    // table body. Omitting it makes the canvas database exactly 16px too short
    // whenever wide columns require horizontal scrolling, which clips the
    // database footer. Read the already rendered scrollbar's layout height;
    // do not derive it from the canvas host and do not add another observer.
    const horizontalScrollbarHeight =
      this.horizontalScrollbarRef.value?.offsetHeight ?? 0;
    return Math.max(dataHeight, bodyHeight) + horizontalScrollbarHeight;
  }

  private readonly onHorizontalScroll = (event: Event) => {
    const target = event.currentTarget;
    if (target instanceof HTMLElement) {
      this.xScrollLeft$.value = target.scrollLeft;
      this.scrollContentWidth$.value = target.scrollWidth;
      this.scrollViewportWidth$.value = target.clientWidth;
    }
  };

  /**
   * Viewport of the horizontal scroll container in client coordinates. The
   * sticky column header lives outside that container, so header drags use
   * this rect to stay inside the visible database block.
   */
  getHorizontalViewportRect(): DOMRect | undefined {
    return this.xScrollContainerRef.value?.getBoundingClientRect();
  }

  /**
   * Scroll the table horizontally by `delta` pixels, clamped to the content
   * range. Keeps the sticky header in sync through `xScrollLeft$`.
   *
   * @returns the applied scroll distance
   */
  scrollHorizontallyBy(delta: number): number {
    const xScrollContainer = this.xScrollContainerRef.value;
    if (!xScrollContainer) {
      return 0;
    }
    const maxScrollLeft =
      xScrollContainer.scrollWidth - xScrollContainer.clientWidth;
    if (maxScrollLeft <= 0) {
      return 0;
    }
    const nextScrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, xScrollContainer.scrollLeft + delta)
    );
    const applied = nextScrollLeft - xScrollContainer.scrollLeft;
    if (applied === 0) {
      return 0;
    }
    xScrollContainer.scrollLeft = nextScrollLeft;
    this.xScrollLeft$.value = nextScrollLeft;
    return applied;
  }

  revealColumn(columnId: string): void {
    const container = this.xScrollContainerRef.value;
    if (!container) return;
    this.scrollHorizontallyBy(
      getColumnRevealScrollDelta({
        columns: this.logic.layoutSnapshot$.value.gridColumns,
        columnId,
        padding: this.logic.root.config.virtualPadding$.value,
        scrollLeft: container.scrollLeft,
        viewportWidth: container.clientWidth,
      })
    );
  }

  private readonly onHorizontalWheel = (event: WheelEvent) => {
    const xScrollContainer = this.xScrollContainerRef.value;
    if (!xScrollContainer) {
      return;
    }

    const maxScrollLeft =
      xScrollContainer.scrollWidth - xScrollContainer.clientWidth;
    if (maxScrollLeft <= 0) {
      return;
    }

    const delta = getHorizontalWheelDelta(event, xScrollContainer.clientWidth);
    if (delta == null) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.scrollHorizontallyBy(delta);
  };

  override render(): TemplateResult {
    const vPadding = this.logic.root.config.virtualPadding$.value;
    const stickyHeaderStyle = styleMap({
      paddingLeft: `${vPadding}px`,
      paddingRight: `${vPadding}px`,
    });
    const stickyHeaderInnerStyle = styleMap({
      left: `${-this.xScrollLeft$.value}px`,
    });
    const wrapperStyle = styleMap({
      marginLeft: `-${vPadding}px`,
      marginRight: `-${vPadding}px`,
    });
    const containerStyle = styleMap({
      paddingLeft: `${vPadding}px`,
      paddingRight: `${vPadding}px`,
      '--dv-horizontal-scroll-left': `${this.xScrollLeft$.value}px`,
    });
    return html`
      <div
        ${ref(this.yScrollContainerRef)}
        class="${styles.tableContainer}"
        data-data-view-body="true"
        data-data-view-body-state=${this.logic.bodyState$.value ?? nothing}
        style="${wrapperStyle}"
      >
        <div class="${styles.stickyColumnHeader}" style="${stickyHeaderStyle}">
          <div
            class="${styles.stickyColumnHeaderInner}"
            style="${stickyHeaderInnerStyle}"
            @wheel="${this.onHorizontalWheel}"
          >
            <virtual-table-header
              .tableViewLogic="${this.logic}"
            ></virtual-table-header>
          </div>
        </div>
        <div
          ${ref(this.xScrollContainerRef)}
          class="${styles.tableBlockTable}"
          data-data-view-body-content="true"
          data-data-view-body-intrinsic="true"
          data-data-view-body-render-root="true"
          @scroll="${this.onHorizontalScroll}"
          @wheel="${handleTableWheel}"
        >
          <div
            class="${styles.tableContainer2}"
            data-data-view-body-render-content="true"
            style="${containerStyle}"
          >
            ${this.renderTable()}
          </div>
        </div>
      </div>
      <dv-horizontal-scrollbar
        ${ref(this.horizontalScrollbarRef)}
        .target=${this.xScrollContainerRef.value}
        .viewportWidth=${this.scrollViewportWidth$.value}
        .contentWidth=${this.scrollContentWidth$.value}
        .label=${'Horizontal table scroll'}
      ></dv-horizontal-scrollbar>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-table-view-ui-virtual': TableViewUI;
  }
}
